import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Settings -> Policies -> Agent Autonomy (v4/api, `App\Settings\Autonomy\
 * AutonomySettings`): ce arata `Autonomy::index` si cele 15 scrieri ale
 * lui, cu aceleasi campuri si aceleasi raspunsuri `{ok, error?}`.
 */
const BASE = '/settings/autonomy'

export type Answer = { ok: boolean; error?: string; id?: number; added?: number; ids?: number[]; skipped?: string[]; count?: number; hits?: PreviewHit[] }
export type Row = Record<string, unknown>

export interface PreviewHit {
  device_id: number
  device: string
  n: number
}

/** `Policy::AD_WRITE_PATHS`; `connector` se salveaza, dar Router il trateaza ca `none`. */
export type AdWritePath = 'none' | 'dc_runbook' | 'connector'

export interface DomainController {
  device_id: number
  name: string
  agent_version: string
  collected_at: string | null
  last_dc_check: { action_id: number; state: string; at: string | null; writable: boolean; host: string } | null
}

/** `AutonomySettings::identityData` - blocul "Identity: Active Directory" (F12). */
export interface DeskIdentity {
  per_company: boolean
  ad_write_path: AdWritePath
  ad_dc_device_id: number
  dc: DomainController | null
  dcs: DomainController[]
  prerequisites: { ok: boolean; items: { key: string; ok: boolean; value: unknown; hint: string }[] } | null
  unknown_verified: { from_address: string; last_seen: string; messages: number }[]
  min_agent: string
  delegations_reviewed_at: string | null
}

export interface AutonomyPage {
  ready: boolean
  switchedOn: boolean
  sensors: Row[]
  sensorsReady: boolean
  playbookReady: boolean
  runbooks: Row[]
  library: Record<string, Row>
  dailyCap: number | null
  isGlobal: boolean
  rules: Row[]
  canScope: boolean
  overrides: Record<string, Row>
  recent: Row[]
  catalog: Row[]
  notifyEmail: string
  autoDiagnose: boolean | null
  companyId: number
  companyName: string
  response: {
    ready: boolean
    scope: 'company' | 'sp'
    sp: number
    matrix: Record<string, Row>
    overrides: Row[]
    types: Row[]
    people: Row[]
    recent: Row[]
    today: Row
    severities: Record<string, string>
  }
  desk: {
    ready: boolean
    scope: 'company' | 'sp'
    sp: number
    policy: Row
    own: boolean
    /** Company scope only; null on the provider row or before the F11 migration. */
    weekly_summary?: boolean | null
    recent: Row[]
    today: Row
    aiReady: boolean
    staff?: { ID: number; name: string; group_id: number }[]
    groups?: { ID: number; name: string }[]
    kinds?: { types: Row[]; statuses: Row[] }
    warnings?: string[]
    identity?: DeskIdentity | null
    /** The desk runbook pack in the vetted catalog: platform rows plus the company's (only platform rows on All companies). */
    desk_catalog?: { seeded: number; executable: number; stale: number }
  }
  deskPaused?: {
    paused: boolean
    by: number
    at: string | null
    reason: string
  }
  heartbeat?: {
    tick: string
    started_at: string | null
    finished_at: string | null
    last_ok_at: string | null
    last_error: string | null
    stale: boolean
  }[]
}

export function autonomyQuery(scope: string, dscope: string) {
  const p = new URLSearchParams()
  if (scope) p.set('scope', scope)
  if (dscope) p.set('dscope', dscope)
  const q = p.toString()
  return queryOptions({
    queryKey: ['settings', 'autonomy', scope, dscope],
    queryFn: () => apiGet<AutonomyPage>(BASE + (q ? `?${q}` : '')),
  })
}

export const autonomyPost = (path: string, fields: Record<string, PostValue>) => apiPost<Answer>(`${BASE}${path}`, fields)
